import '@/App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { LangProvider } from './context/LangContext';
import { SAAuthProvider } from './context/SAAuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { VoyageProvider } from './context/VoyageContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
// === Eager (chemin critique : auth + landing publique) ===
import Login from './pages/Login';
import Landing from './pages/saas/Landing';
import CGU from './pages/legal/CGU';
import CGV from './pages/legal/CGV';
import RGPD from './pages/legal/RGPD';
import MentionsLegales from './pages/legal/MentionsLegales';
import PublicTarifs from './pages/public/PublicTarifs';
// === Lazy (toutes les autres pages) — P2 #7 lazy loading ===
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardChef = lazy(() => import('./pages/DashboardChef'));
const Clients = lazy(() => import('./pages/Clients'));
const Passagers = lazy(() => import('./pages/Passagers'));
const Manuel = lazy(() => import('./pages/Manuel'));
const CataloguePrint = lazy(() => import('./pages/CataloguePrint'));
const VoyagesOrganises = lazy(() => import('./pages/VoyagesOrganises'));
const Fournisseurs = lazy(() => import('./pages/Fournisseurs'));
const SupplierSituation = lazy(() => import('./pages/fournisseurs/SupplierSituation'));
const FournisseursHub = lazy(() => import('./pages/fournisseurs/FournisseursHub'));
const AuditComptableFournisseurs = lazy(() => import('./pages/AuditComptableFournisseurs'));
const Ventes = lazy(() => import('./pages/Ventes'));
const Paiements = lazy(() => import('./pages/Paiements'));
const OfficesPage = lazy(() => import('./pages/OfficesPage'));
const PaiementsB2B = lazy(() => import('./pages/PaiementsB2B'));
const Remboursements = lazy(() => import('./pages/Remboursements'));
const CreancesClients = lazy(() => import('./pages/CreancesClients'));
const FinancialAlerts = lazy(() => import('./pages/FinancialAlerts'));
const Rapports = lazy(() => import('./pages/Rapports'));
const Comptabilite = lazy(() => import('./pages/Comptabilite'));
const Charges = lazy(() => import('./pages/Charges'));
const Agents = lazy(() => import('./pages/Agents'));
const Parametres = lazy(() => import('./pages/Parametres'));
const ExportDonnees = lazy(() => import('./pages/ExportDonnees'));
const Billetterie = lazy(() => import('./pages/Billetterie'));
const HistoriqueEmails = lazy(() => import('./pages/HistoriqueEmails'));
const Devis = lazy(() => import('./pages/Devis'));
const Chat = lazy(() => import('./pages/Chat'));
const BonsCommande = lazy(() => import('./pages/BonsCommande'));
const AmadeusImport = lazy(() => import('./pages/AmadeusImport'));
const AdminAuditLog = lazy(() => import('./pages/AdminAuditLog'));
const ProspectsList = lazy(() => import('./pages/prospect/ProspectsList'));
const ProspectDetail = lazy(() => import('./pages/prospect/ProspectDetail'));
const ProspectsKanban = lazy(() => import('./pages/prospect/ProspectsKanban'));
const RelancesPanel = lazy(() => import('./pages/prospect/RelancesPanel'));
const DashboardCommercial = lazy(() => import('./pages/prospect/DashboardCommercial'));
const SuiviVoyage = lazy(() => import('./pages/SuiviVoyage'));
const Guide = lazy(() => import('./pages/Guide'));
const GuideScan = lazy(() => import('./pages/Guide').then(m => ({ default: m.GuideScan })));
const GuideAccess = lazy(() => import('./pages/GuideAccess'));
const GuideAccessScan = lazy(() => import('./pages/GuideAccessScan'));
const SupplierTracking = lazy(() => import('./pages/SupplierTracking'));
const Satisfaction = lazy(() => import('./pages/Satisfaction'));
const Billing = lazy(() => import('./pages/Billing'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const BillingCancel = lazy(() => import('./pages/BillingCancel'));
// SaaS Public + Super Admin
const Inscription = lazy(() => import('./pages/saas/Inscription'));
const Cgu = lazy(() => import('./pages/legal/Cgu'));
const Confidentialite = lazy(() => import('./pages/legal/Confidentialite'));
const SecurityPublic = lazy(() => import('./pages/saas/Security'));
const AgencePublicCatalogue = lazy(() => import('./pages/AgencePublicCatalogue'));
const SALogin = lazy(() => import('./pages/super_admin/SALogin'));
const SA2FASetup = lazy(() => import('./pages/super_admin/SA2FASetup'));
const SALayout = lazy(() => import('./pages/super_admin/SALayout'));
const SADashboard = lazy(() => import('./pages/super_admin/SADashboard'));
const SAMonitoring = lazy(() => import('./pages/super_admin/SAMonitoring'));
const SAPerformance = lazy(() => import('./pages/super_admin/SAPerformance'));
const SATenants = lazy(() => import('./pages/super_admin/SATenants'));
const SAFinance = lazy(() => import('./pages/super_admin/SAFinance'));
const SAPertesGestes = lazy(() => import('./pages/super_admin/SAPertesGestes'));
const SAUsers = lazy(() => import('./pages/super_admin/SAUsers'));
const SAPlans = lazy(() => import('./pages/super_admin/SAPlans'));
const SABillingConfig = lazy(() => import('./pages/super_admin/SABillingConfig'));
const SAModules = lazy(() => import('./pages/super_admin/SAModules'));
const SAConfig = lazy(() => import('./pages/super_admin/SAConfig'));
const SADownloads = lazy(() => import('./pages/super_admin/SADownloads'));
const SABackup = lazy(() => import('./pages/super_admin/SABackup'));
const SAStorage = lazy(() => import('./pages/super_admin/SAStorage'));
const SAAgencyImport = lazy(() => import('./pages/super_admin/SAAgencyImport'));
const SAPilotOnboarding = lazy(() => import('./pages/super_admin/SAPilotOnboarding'));
const SAMaintenance = lazy(() => import('./pages/super_admin/SAMaintenance'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const OnboardingWizard = lazy(() => import('./pages/saas/OnboardingWizard'));

// Skeleton de chargement (minimaliste, palette CRM)
const PageSkeleton = () => (
  <div
    data-testid="page-skeleton"
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', color: '#94a3b8', fontFamily: 'DM Sans, system-ui, sans-serif',
      fontSize: 14, letterSpacing: '0.05em',
    }}
  >
    <div style={{
      width: 24, height: 24, borderRadius: '50%',
      border: '3px solid #e2e8f0', borderTopColor: '#2563eb',
      animation: 'spin 0.8s linear infinite', marginRight: 12,
    }} />
    Chargement…
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  return (
    <LangProvider>
    <AuthProvider>
    <ThemeProvider>
    <SAAuthProvider>
    <VoyageProvider>
      <Toaster position="top-right" richColors closeButton duration={4000} />
      <BrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {/* Public marketing & signup */}
          <Route path="/" element={<Landing />} />
          <Route path="/inscription" element={<Inscription />} />
          <Route path="/cgu" element={<Cgu />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/security" element={<SecurityPublic />} />

          {/* Pages légales publiques */}
          <Route path="/cgu" element={<CGU />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/rgpd" element={<RGPD />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/legal/cgu" element={<CGU />} />
          <Route path="/legal/cgv" element={<CGV />} />
          <Route path="/legal/rgpd" element={<RGPD />} />
          <Route path="/legal/mentions-legales" element={<MentionsLegales />} />

          {/* Page Tarifs publique (SaaS monétisation V1) */}
          <Route path="/tarifs" element={<PublicTarifs />} />

          {/* Super Admin portal */}
          <Route path="/super-admin/login" element={<SALogin />} />
          <Route path="/super-admin/2fa/setup" element={<SA2FASetup />} />
          <Route path="/super-admin" element={<SALayout />}>
            <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
            <Route path="dashboard" element={<SADashboard />} />
            <Route path="monitoring" element={<SAMonitoring />} />
            <Route path="performance" element={<SAPerformance />} />
            <Route path="tenants" element={<SATenants />} />
            <Route path="finance" element={<SAFinance />} />
            <Route path="pertes-gestes" element={<SAPertesGestes />} />
            <Route path="users" element={<SAUsers />} />
            <Route path="plans" element={<SAPlans />} />
            <Route path="billing-config" element={<SABillingConfig />} />
            <Route path="modules" element={<SAModules />} />
            <Route path="config" element={<SAConfig />} />
            <Route path="backup" element={<SABackup />} />
            <Route path="storage" element={<SAStorage />} />
            <Route path="agency-import" element={<SAAgencyImport />} />
            <Route path="pilot-onboarding" element={<SAPilotOnboarding />} />
            <Route path="maintenance" element={<SAMaintenance />} />
            <Route path="downloads" element={<SADownloads />} />
          </Route>

          {/* App authentifie (agence) */}
          <Route path="/login" element={<Login />} />
          {/* Vitrine publique de l'agence : /a/{slug} */}
          <Route path="/a/:slug" element={<AgencePublicCatalogue />} />
          {/* Path-based routing : /a/{slug}/login -> Login avec branding pre-charge */}
          <Route path="/a/:slug/login" element={<Login />} />
          <Route path="/documentation" element={<Manuel />} />
          <Route path="/catalogue/print" element={<CataloguePrint />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
          </Route>

          {/* Routes app sous Layout (gardent leurs paths existants pour compat) */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard-chef" element={<ProtectedRoute requireChef={true}><DashboardChef /></ProtectedRoute>} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/passagers" element={<Passagers />} />
            <Route path="/voyages" element={<VoyagesOrganises />} />
            <Route path="/fournisseurs" element={<Fournisseurs />} />
            <Route path="/fournisseurs/:id/situation" element={<SupplierSituation />} />
            <Route path="/fournisseurs-hub" element={<FournisseursHub />} />
            <Route path="/audit/comptable-fournisseurs" element={<AuditComptableFournisseurs />} />
            <Route path="/ventes" element={<Ventes />} />
            <Route path="/devis" element={<Devis />} />
            <Route path="/paiements" element={<Paiements />} />
            <Route path="/offices" element={<ProtectedRoute requirePermission="gerer_offices"><OfficesPage /></ProtectedRoute>} />
            <Route path="/creances-clients" element={<CreancesClients />} />
            <Route path="/alertes-financieres" element={<FinancialAlerts />} />
            <Route path="/paiements-b2b" element={<ProtectedRoute requirePermission="b2b_paiements_fournisseur"><PaiementsB2B /></ProtectedRoute>} />
            <Route path="/billetterie" element={<Billetterie />} />
            <Route path="/remboursements" element={<Remboursements />} />
            <Route path="/rapports" element={<Rapports />} />
            <Route path="/comptabilite" element={<Comptabilite />} />
            <Route path="/charges" element={<Charges />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/parametres" element={<Parametres />} />
            <Route path="/parametres/export-donnees" element={<ExportDonnees />} />
            <Route path="/historique-emails" element={<HistoriqueEmails />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/bons-commande" element={<BonsCommande />} />
            <Route path="/amadeus" element={<AmadeusImport />} />
            <Route path="/audit-logs" element={<ProtectedRoute requireChef={true}><AdminAuditLog /></ProtectedRoute>} />
            <Route path="/prospects" element={<ProspectsList />} />
            <Route path="/prospects/:id" element={<ProspectDetail />} />
            <Route path="/commercial/kanban" element={<ProspectsKanban />} />
            <Route path="/commercial/relances" element={<RelancesPanel />} />
            <Route path="/commercial/dashboard" element={<DashboardCommercial />} />
            <Route path="/suivi-voyage" element={<ProtectedRoute requirePermission="b2b_suivi_voyage"><SuiviVoyage /></ProtectedRoute>} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/marketplace" element={<Marketplace />} />
          </Route>

          {/* Pages Stripe redirection (sans Layout) */}
          <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
          <Route path="/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>}/>

          {/* Onboarding Wizard plein-ecran (chef-only) */}
          <Route path="/onboarding-wizard" element={<ProtectedRoute requireChef={true}><OnboardingWizard /></ProtectedRoute>} />

          {/* Pages mode Guide (mobile, sans Layout/Sidebar) */}
          <Route path="/guide" element={<ProtectedRoute requirePermission="b2b_mode_guide"><Guide /></ProtectedRoute>} />
          <Route path="/guide/voyage/:voyageId" element={<ProtectedRoute requirePermission="b2b_mode_guide"><GuideScan /></ProtectedRoute>} />
          {/* Acces guide externe (publics — JWT scope au voyage) */}
          <Route path="/g/:token" element={<GuideAccess />} />
          <Route path="/g/scan/:voyageId" element={<GuideAccessScan />} />
          {/* Acces fournisseur (public — saisie numeros de chambre) */}
          <Route path="/s/:token" element={<SupplierTracking />} />
          {/* Sondage satisfaction client (public) */}
          <Route path="/sat/:token" element={<Satisfaction />} />
          {/* Fallback : routes obsolètes/inconnues → redirige vers le dashboard.
              iter89 P3 — supprime les anciens redirects paiements/creances/etat-fournisseurs.
              L'utilisateur se retrouve sur l'accueil au lieu d'une page blanche. */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </VoyageProvider>
    </SAAuthProvider>
    </ThemeProvider>
    </AuthProvider>
    </LangProvider>
  );
}

export default App;
